import moment, { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntercom } from 'react-use-intercom'

import { useAppDispatch, useAppSelector } from '../App/Hooks/reduxHooks'
import {
  activityListSelector,
  activityMetaDataSelector,
} from '../App/store/features/activity/activitySlice'
import { customerListSelector } from '../App/store/features/customer/customerSlice'
import { ICurrentUser } from '../App/store/features/person/interface/ICurrentUser'
import { Person } from '../App/store/features/person/IPerson'
import {
  personAbsencesSelector,
  personCurrentSelector,
  personHoursSelector,
  personListSelector,
} from '../App/store/features/person/personSlice'
import { Flex } from '../Common/Flex'
import { DATE_STORE_KEY_FORMAT } from '../Common/settings/dateFormats'
import { Spinner } from '../Common/Spinner'
import { IEstablishment } from '../Establishment/interface/IEstablishment'
import { activeEstablishmentSelector } from '../Establishment/Store/reducer'

import { CalendarHeader } from './CalendarHeader'
import { Day } from './Day'
import { CrudPanel } from './EventCrudPanel/CrudPanel'
import { EventArea } from './Events/EventArea'
import { intercomBootDetails } from './settings/intercomBootDetails'
import { getUserSettings, setUserSettings } from './settings/userSettings'
import {
  calendarOpenFormDetailsSelector,
  calendarSettingsSelector,
  calendarStateSelector,
  eventPositionChange,
} from './store/calendarSlice'
import { TimeColumn } from './TimeColumn'
import { IEmptySlotClick } from './types'
import { appointmentCoordinateUpdate } from './utils/crudHelper/appointmentCoordinateUpdate'
import { blockCoordinateUpdate } from './utils/crudHelper/blockCoordinateUpdate'
import { copyUpdate } from './utils/crudHelper/copyUpdate'
import { createEvent } from './utils/crudHelper/createEvent'
import { cutUpdate } from './utils/crudHelper/cutUpdate'

export const Calendar = () => {
  const { calendarDate } = getUserSettings()
  const [calculatedPixelsPerHour, setCalculatedPixelsPerHour] =
    useState<number>(0)

  const [selectedDate, setSelectedDate] = useState<Moment>(moment(calendarDate))
  const [lastEmptySlotClick, setLastEmptySlotClick] = useState<
    IEmptySlotClick | undefined
  >()

  const [filteredPersons, setFilteredPersons] = useState<Person[]>()

  const dispatch = useAppDispatch()
  const currentEstablishment = useAppSelector(activeEstablishmentSelector)
  const activityList = useAppSelector(activityListSelector)
  const activityMetaData = useAppSelector(activityMetaDataSelector)
  const workingHours = useAppSelector(personHoursSelector)
  const currentUser = useAppSelector(personCurrentSelector)
  const personList = useAppSelector(personListSelector)
  const absences = useAppSelector(personAbsencesSelector)
  const customerList = useAppSelector(customerListSelector)

  const { amountOfCalendarDays, inActivePersonIds, colorContrast } =
    useAppSelector(calendarSettingsSelector)

  const openFormDetails = useAppSelector(calendarOpenFormDetailsSelector)
  const evenPositionChange = useAppSelector(eventPositionChange)
  const blockCategories = useAppSelector(calendarStateSelector).eventsByDay

  useEffect(() => {
    if (
      evenPositionChange.state === 'update' &&
      typeof filteredPersons !== 'undefined' &&
      typeof evenPositionChange.eventData !== 'undefined' &&
      typeof evenPositionChange.eventCoordinates !== 'undefined'
    ) {
      const { eventData, eventCoordinates } = evenPositionChange
      if (eventData.type === 'appointment') {
        appointmentCoordinateUpdate(
          eventData,
          eventCoordinates,
          filteredPersons,
          dispatch,
        )
        return
      }
      if (eventData.type === 'block') {
        blockCoordinateUpdate(
          blockCategories,
          eventData,
          eventCoordinates,
          filteredPersons,
          dispatch,
        )
      }
    }
  }, [blockCategories, dispatch, evenPositionChange, filteredPersons])

  const { boot } = useIntercom()
  const bootIntercom = useCallback(
    (establishmentDetails: IEstablishment, userDetails: ICurrentUser) =>
      boot(intercomBootDetails(establishmentDetails, userDetails)),
    [boot],
  )

  // TODO what's this?
  const [selectedStartDate, selectedEndDate] = useMemo(() => {
    setUserSettings({
      calendarDate: selectedDate.format(DATE_STORE_KEY_FORMAT),
    })
    if (typeof amountOfCalendarDays === 'undefined') {
      return [selectedDate.clone(), selectedDate.clone().add(3, 'days')]
    }
    if (amountOfCalendarDays === 7) {
      return [
        selectedDate.clone().startOf('isoWeek'),
        selectedDate.clone().endOf('isoWeek'),
      ]
    }

    return [
      selectedDate.clone(),
      selectedDate.clone().add(amountOfCalendarDays, 'days'),
    ]
  }, [selectedDate, amountOfCalendarDays])

  useEffect(() => {
    if (personList) {
      setFilteredPersons(
        personList.filter(person => !inActivePersonIds.includes(person.id)),
      )
    }
  }, [inActivePersonIds, personList])

  useEffect(() => {
    if (
      typeof currentEstablishment !== 'undefined' &&
      typeof currentUser !== 'undefined'
    ) {
      bootIntercom(currentEstablishment, currentUser)
    }
  }, [bootIntercom, currentEstablishment, currentUser])

  const onEmptySlotClicked = (person: Person, clickedMoment: Moment) => {
    setLastEmptySlotClick({ person, clickedMoment })
  }

  useMemo(() => {
    if (
      typeof lastEmptySlotClick !== 'undefined' &&
      typeof customerList !== 'undefined' &&
      typeof activityList !== 'undefined'
    ) {
      if (openFormDetails.type === 'copy') {
        copyUpdate(openFormDetails, lastEmptySlotClick, activityList, dispatch)
      }
      if (openFormDetails.type === 'cut') {
        cutUpdate(openFormDetails, lastEmptySlotClick, dispatch)
      }
      createEvent(lastEmptySlotClick, dispatch)
    }
  }, [lastEmptySlotClick])

  // TODO CHECK HOW TO MAKE DYNAMIC
  const hardCodedTimeDivision = 15

  const dayComponents = useMemo(() => {
    if (
      typeof personList !== 'undefined' &&
      typeof amountOfCalendarDays !== 'undefined'
    ) {
      const newDayComponents = []
      for (let i = 0; i < amountOfCalendarDays; i += 1) {
        newDayComponents.push(
          <Day
            key={`day${i}`}
            date={selectedStartDate.clone().add(i, 'days')}
            persons={personList}
            timeDivision={hardCodedTimeDivision}
            workingHours={workingHours}
            absences={absences}
            onEmptySlotClicked={onEmptySlotClicked}
          />,
        )
      }

      return newDayComponents
    }
    return []
  }, [
    personList,
    workingHours,
    absences,
    amountOfCalendarDays,
    selectedStartDate,
  ])

  if (
    !personList ||
    !filteredPersons ||
    typeof amountOfCalendarDays === 'undefined' ||
    typeof colorContrast === 'undefined' ||
    typeof inActivePersonIds === 'undefined' ||
    typeof activityMetaData === 'undefined'
  ) {
    return <Spinner />
  }
  // NUMBERS FOR CALC
  const amountOfColumns: number = amountOfCalendarDays * filteredPersons.length

  return (
    <div>
      <CrudPanel personList={personList} openEventDetails={openFormDetails} />

      <CalendarHeader
        personsList={personList}
        filteredPersons={filteredPersons}
        selectedDate={selectedDate}
        startDate={selectedStartDate}
        amountOfDays={amountOfCalendarDays}
        onDateChanged={date => setSelectedDate(date)}
      />
      <Flex flex={1}>
        <TimeColumn
          calculatedPixelsPerHour={calculatedPixelsPerHour}
          contrastColor={colorContrast}
        />
        {dayComponents}
        <EventArea
          filteredPersons={filteredPersons}
          inActivePersonIds={inActivePersonIds}
          amountOfColumns={amountOfColumns}
          timeDivision={hardCodedTimeDivision}
          amountOfDays={amountOfCalendarDays}
          selectedStartDate={selectedStartDate}
          setCalculatedPixelsPerHour={setCalculatedPixelsPerHour}
          activityMetaData={activityMetaData}
        />
      </Flex>
    </div>
  )
}
